import { useState, useEffect, useCallback, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { getDeviceFingerprint, clearDeviceFingerprint } from "@/lib/deviceFingerprint";
import type { User, Session } from "@supabase/supabase-js";
import {
  SESSION_TOKEN_KEY,
  SESSION_EXPIRES_KEY,
  getAdminSessionItem,
  setAdminSessionItem,
  clearAdminSessionStore,
} from "@/lib/adminSessionStore";
import { maybeHandleSupersededFromPayload } from "@/lib/adminSessionRecovery";

export const useAdminAuth = () => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [roleLoading, setRoleLoading] = useState(true);
  const [sessionToken, setSessionToken] = useState<string | null>(null);
  const [sessionValid, setSessionValid] = useState(false);

  // Load session token from persistent storage (survives browser restarts).
  useEffect(() => {
    const storedToken = getAdminSessionItem(SESSION_TOKEN_KEY);
    if (storedToken) {
      setSessionToken(storedToken);
    }
  }, []);

  const hasCheckedRole = useRef(false);

  // Sync role when user changes (defer supabase calls out of the auth callback)
  useEffect(() => {
    const checkRole = async () => {
      // Only show loading on first check, not on token refreshes
      if (!hasCheckedRole.current) {
        setRoleLoading(true);
      }
      if (!user) {
        setIsAdmin(false);
        setRoleLoading(false);
        hasCheckedRole.current = true;
        return;
      }
      const { data: roleData } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', user.id)
        .eq('role', 'admin')
        .maybeSingle();
      setIsAdmin(!!roleData);
      setRoleLoading(false);
      hasCheckedRole.current = true;
    };
    // Defer to avoid deadlocks with auth listener
    const t = setTimeout(checkRole, 0);
    return () => clearTimeout(t);
  }, [user]);

  useEffect(() => {
    // Listener (sync only)
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, sess) => {
      setSession(sess);
      setUser(sess?.user ?? null);
    });

    // Initial session load
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  // Set admin session after successful login
  const setAdminSession = useCallback((token: string, expiresAt: string) => {
    setAdminSessionItem(SESSION_TOKEN_KEY, token);
    setAdminSessionItem(SESSION_EXPIRES_KEY, expiresAt);
    setSessionToken(token);
    setSessionValid(true);
  }, []);

  // Clear admin session on logout or expiry
  const clearAdminSession = useCallback(() => {
    clearAdminSessionStore(['admin-tab-hidden-at', 'admin-requires-reauth']);
    // Clear persisted dashboard tab so next user lands on Dashboard
    localStorage.removeItem('admin-active-tab');
    clearDeviceFingerprint();
    setSessionToken(null);
    setSessionValid(false);
  }, []);

  // Validate session with server.
  //
  // Resiliency: never clear the admin session on a network error / throw — the
  // user might just be offline or hitting a Supabase cold-start. Only clear
  // when the server explicitly says the session is dead.
  const validateSession = useCallback(async (): Promise<boolean> => {
    const storedToken = getAdminSessionItem(SESSION_TOKEN_KEY);
    if (!storedToken) {
      setSessionValid(false);
      return false;
    }

    // Check if session has expired locally first
    const expiresAt = getAdminSessionItem(SESSION_EXPIRES_KEY);
    if (expiresAt && new Date(expiresAt) < new Date()) {
      clearAdminSession();
      return false;
    }

    const FATAL_CODES = new Set(["NOT_ADMIN", "INVALID_SESSION"]);

    try {
      const fingerprint = await getDeviceFingerprint();
      let { data: { session: currentSession } } = await supabase.auth.getSession();

      if (!currentSession?.access_token) {
        // One refresh attempt before giving up.
        await supabase.auth.refreshSession().catch(() => {});
        ({ data: { session: currentSession } } = await supabase.auth.getSession());
      }
      if (!currentSession?.access_token) {
        // No Supabase access token right now, but admin token is on disk.
        // Don't destroy it — the next mount can retry.
        setSessionValid(false);
        return false;
      }

      const { data, error } = await supabase.functions.invoke('admin-auth-validate', {
        headers: {
          'x-session-token': storedToken,
          'x-device-fingerprint': fingerprint,
          'Authorization': `Bearer ${currentSession.access_token}`
        }
      });

      if (error || !data?.valid) {
        const errCtx = (error as { context?: { response?: Response } } | null)?.context;
        if (errCtx?.response) {
          const body = await errCtx.response.clone().json().catch(() => null);
          if (maybeHandleSupersededFromPayload(body)) return false;
        }
        if (data && maybeHandleSupersededFromPayload(data)) return false;

        const code = (data?.code ?? "") as string;
        if (FATAL_CODES.has(code)) {
          clearAdminSession();
          return false;
        }
        // Non-fatal: keep the token, just mark current validation as failed.
        console.warn('Session validation rejected (non-fatal)', error || data);
        setSessionValid(false);
        return false;
      }

      setSessionValid(true);
      return true;
    } catch (e) {
      // Network / offline / cold start. Keep the token.
      console.warn('Session validation threw — keeping token', e);
      setSessionValid(false);
      return false;
    }
  }, [clearAdminSession]);

  // Get headers for authenticated admin API calls
  // Throws 'NO_SESSION' error if session is not available
  const getAuthHeaders = useCallback(async () => {
    const storedToken = getAdminSessionItem(SESSION_TOKEN_KEY);
    
    // If no session token, user is not authenticated
    if (!storedToken) {
      throw new Error('NO_SESSION');
    }
    
    const { data: { session: currentSession } } = await supabase.auth.getSession();
    
    // If no Supabase session, user is logged out
    if (!currentSession?.access_token) {
      throw new Error('NO_SESSION');
    }
    
    const fingerprint = await getDeviceFingerprint();
    
    return {
      'x-session-token': storedToken,
      'x-device-fingerprint': fingerprint,
      'Authorization': `Bearer ${currentSession.access_token}`
    };
  }, []);

  const signOut = async () => {
    const storedToken = getAdminSessionItem(SESSION_TOKEN_KEY);
    
    // Clear local session FIRST (before API calls) to prevent race conditions
    clearAdminSession();
    
    // Call server to invalidate session
    if (storedToken) {
      try {
        const { data: { session: currentSession } } = await supabase.auth.getSession();
        await supabase.functions.invoke('admin-logout', {
          headers: {
            'x-session-token': storedToken,
            'Authorization': currentSession?.access_token ? `Bearer ${currentSession.access_token}` : ''
          }
        });
      } catch (e) {
        console.error('Logout API error:', e);
      }
    }
    
    await supabase.auth.signOut();
  };

  return { 
    user, 
    session, 
    isAdmin, 
    loading: loading || roleLoading, 
    sessionToken,
    sessionValid,
    signOut,
    setAdminSession,
    clearAdminSession,
    validateSession,
    getAuthHeaders
  };
};
